import { useCallback } from 'react';

/**
 * Hook to handle parsing QR code data with improved error handling
 * and support for various QR code formats including the LHP- prefix.
 */
export const useQrCodeParser = () => {
  /**
   * Parse the QR code data and extract the confirmation code
   * This function handles various formats of QR codes including 
   * direct codes, URLs, and codes with prefixes
   */
  const parseQrData = useCallback((qrData: string): string | null => {
    // Safety check for null/empty data
    if (!qrData || typeof qrData !== 'string' || qrData.trim() === '') {
      console.warn('Empty or invalid QR data received');
      return null;
    }
    
    try {
      // If qrData is a URL, try to extract parameters
      if (qrData.startsWith('http')) {
        try {
          const url = new URL(qrData);
          // Check for code parameter in URL
          const codeParam = url.searchParams.get('code');
          if (codeParam) {
            // If we found a code parameter, normalize and return it
            return normalizeCode(codeParam);
          }
          
          // Check for last path segment as potential code
          const pathSegments = url.pathname.split('/').filter(Boolean);
          if (pathSegments.length > 0) {
            const lastSegment = pathSegments[pathSegments.length - 1];
            if (lastSegment && lastSegment.length > 3) {
              return normalizeCode(lastSegment);
            }
          }
        } catch (error) {
          console.warn('Failed to parse URL from QR code:', error);
          // Continue to check if the entire string is a code
        }
      }
      
      // Direct code handling (already a code like LHP-ABCDEF)
      return normalizeCode(qrData);
      
    } catch (error) {
      console.error('Error parsing QR data:', error);
      return null;
    }
  }, []);
  
  /**
   * Normalize the code by ensuring it has the LHP- prefix
   * and is properly formatted
   */
  const normalizeCode = useCallback((code: string): string => {
    // Trim any whitespace
    let normalizedCode = code.trim();
    
    // Remove any accents
    normalizedCode = normalizedCode.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    // Convert to uppercase
    normalizedCode = normalizedCode.toUpperCase();
    
    // Add LHP- prefix if missing
    if (!normalizedCode.startsWith('LHP-')) {
      normalizedCode = `LHP-${normalizedCode}`;
    }
    
    // If code contains LHP- more than once, keep only the first occurrence and what follows
    const matches = normalizedCode.match(/LHP-([A-Z0-9]+)/);
    if (matches && matches[1]) {
      normalizedCode = `LHP-${matches[1]}`;
    }
    
    return normalizedCode;
  }, []);
  
  return { parseQrData, normalizeCode };
};
